<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue';
import api from '~/api';

const emit = defineEmits<{
  (e: 'result', payload: { order_id: string; task_id?: string; results: { url: string }[] }): void;
  (e: 'state', s: { canGenerate: boolean; label: string; costLabel: string; busy: boolean }): void;
}>();
const { t } = useI18n();

// ─── Provider model definitions ─────────────────────────────
type T2IProvider = 'gemini' | 'seedream-4.0' | 'seedream-4.5' | 'flux-kontext' | 'wan-2.2' | 'nano-banana-pro' | 'vidsoda-5' | 'aipose-image';

const PROVIDERS: { id: T2IProvider; label: string; badge?: string }[] = [
  { id: 'vidsoda-5', label: 'Bestfaceswap 5.0', badge: 'HOT' },
  { id: 'seedream-4.5', label: 'Seedream 4.5', badge: 'NEW' },
  { id: 'seedream-4.0', label: 'Seedream 4.0' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'flux-kontext', label: 'Flux Kontext' },
  { id: 'wan-2.2', label: 'Wan 2.2' },
  { id: 'nano-banana-pro', label: 'Nano Banana Pro' },
  { id: 'aipose-image', label: 'Bestfaceswap 4.0' },
];

const ASPECT_OPTIONS = ['1:1', '3:4', '4:3', '9:16', '16:9', '2:3', '3:2'] as const;
const RESOLUTION_OPTIONS = ['1K', '2K', '4K'] as const;
const QUANTITY_OPTIONS = [1, 2, 3, 4] as const;
const STYLE_CDN = 'https://cdn.treekee.com/shared/text-to-image/styles';
const VIDSODA_STYLES = [
  { id: 'professional', label: 'Professional', img: `${STYLE_CDN}/style_professional.webp` },
  { id: 'realistic', label: 'Realistic', img: `${STYLE_CDN}/style_realistic.webp` },
  { id: 'revolution', label: 'Revolution', img: `${STYLE_CDN}/style_revolution.webp` },
  { id: 'anime', label: 'Anime', img: `${STYLE_CDN}/style_anime.webp` },
  { id: 'reality', label: 'Reality', img: `${STYLE_CDN}/style_reality.webp` },
  { id: 'basic', label: 'Basic', img: `${STYLE_CDN}/style_basic.webp` },
] as const;

// ─── Preset prompts ─────────────────────────────────────────
const PRESET_PROMPTS = [
  'A beautiful sunset over the ocean with golden light reflecting on waves',
  'A futuristic cyberpunk city at night with neon lights and rain',
  'A serene Japanese garden with cherry blossoms and a stone path',
  'A majestic dragon flying over snow-capped mountains',
  'A cozy cabin in a winter forest with warm light glowing from windows',
  'An astronaut floating in space with Earth in the background',
  'A mystical forest with glowing mushrooms and fireflies at twilight',
  'A steampunk airship flying through clouds at golden hour',
];

// ─── State ──────────────────────────────────────────────────
const provider = ref<T2IProvider>('vidsoda-5');
const prompt = ref('');
const aspect = ref<typeof ASPECT_OPTIONS[number]>('1:1');
const resolution = ref<typeof RESOLUTION_OPTIONS[number]>('2K');
const quantity = ref<number>(1);
const style = ref('');
const sharpen = ref(false);
const quality = ref<'HD' | 'FHD'>('HD');
const styleMode = ref(false);

const submitting = ref(false);
const errorMsg = ref('');

// ─── Provider-conditional visibility ────────────────────────
const showResolution = computed(() => ['nano-banana-pro', 'seedream-4.0', 'seedream-4.5', 'vidsoda-5'].includes(provider.value));
const showStyle = computed(() => provider.value === 'vidsoda-5');
const showSharpen = computed(() => provider.value === 'vidsoda-5');
const showHdFhd = computed(() => provider.value === 'aipose-image');
const showStyleMode = computed(() => provider.value === 'aipose-image');

// ─── Cost calculation ───────────────────────────────────────
const costPerImage = computed(() => {
  const p = provider.value;
  const r = resolution.value;
  if (p === 'nano-banana-pro') return r === '4K' ? 30 : r === '2K' ? 20 : 15;
  if (p === 'seedream-4.0' || p === 'seedream-4.5') return r === '4K' ? 12 : 6;
  if (p === 'vidsoda-5') return r === '4K' ? 12 : r === '2K' ? 8 : 4;
  if (p === 'wan-2.2') return 5;
  return 4; // gemini, flux-kontext, aipose-image
});
const totalCost = computed(() => costPerImage.value * quantity.value);
const canGenerate = computed(() => !submitting.value && prompt.value.trim().length >= 3);

// ─── Helpers ────────────────────────────────────────────────
function ratioBox(id: string) {
  const [w, h] = id.split(':').map(Number);
  const max = 16;
  return {
    width: `${w >= h ? max : Math.round((max * w) / h)}px`,
    height: `${h >= w ? max : Math.round((max * h) / w)}px`,
  };
}

function randomPrompt() {
  const idx = Math.floor(Math.random() * PRESET_PROMPTS.length);
  prompt.value = PRESET_PROMPTS[idx];
}

// ─── Generate ───────────────────────────────────────────────
async function generate() {
  if (!canGenerate.value) return;
  errorMsg.value = '';
  submitting.value = true;

  try {
    const payload: Record<string, any> = {
      provider: provider.value,
      prompt: prompt.value.trim(),
      ratio: aspect.value,
      quantity: quantity.value,
    };
    if (showResolution.value) payload.resolution = resolution.value;
    if (showStyle.value && style.value) payload.style = style.value;
    if (showSharpen.value) payload.sharpen = sharpen.value;
    if (showHdFhd.value) payload.quality = quality.value;
    if (showStyleMode.value) payload.styleMode = styleMode.value;

    const r = await api.createTextToImage(payload);
    submitting.value = false;

    // Emit immediately — gallery will show PROCESSING card and poll for async tasks
    emit('result', {
      order_id: r.order_id,
      task_id: r.task_id,
      results: r.results || [],
    });
  } catch (e: any) {
    submitting.value = false;
    errorMsg.value = e?.message || t('imageGen.generationFailed');
  }
}

// ─── State mirror to parent ─────────────────────────────────
watchEffect(() => {
  emit('state', {
    canGenerate: canGenerate.value,
    label: submitting.value ? t('imageGen.submitting') : t('imageGen.generate'),
    costLabel: t('imageGen.creditsCount', { n: totalCost.value }),
    busy: submitting.value,
  });
});
defineExpose({ generate });
</script>

<template>
  <div class="panel">
    <div class="head">
      <h2>{{ t('imageGen.textToImage') }}</h2>
    </div>

    <!-- Prompt -->
    <div class="section">
      <div class="prompt-head">
        <h3>Prompt</h3>
        <button type="button" class="random-btn" @click="randomPrompt">{{ t('imageGen.presetPrompt') }}</button>
      </div>
      <textarea v-model="prompt" rows="3" :placeholder="t('imageGen.promptPlaceholder')" />
    </div>

    <!-- Model -->
    <div class="section">
      <h3>{{ t('imageGen.model') }}</h3>
      <select v-model="provider" class="model-select">
        <option v-for="p in PROVIDERS" :key="p.id" :value="p.id">{{ p.label }}{{ p.badge ? ` [${p.badge}]` : '' }}</option>
      </select>
    </div>

    <!-- Ratio -->
    <div class="section">
      <h3>{{ t('imageGen.ratio') }}</h3>
      <div class="ratio-grid">
        <button
          v-for="a in ASPECT_OPTIONS" :key="a"
          class="ratio-btn"
          :class="{ active: aspect === a }"
          @click="aspect = a"
        >
          <span class="ratio-box" :style="ratioBox(a)" />
          <span>{{ a }}</span>
        </button>
      </div>
    </div>

    <!-- Quantity -->
    <div class="section">
      <h3>{{ t('imageGen.quantity') }}</h3>
      <div class="qty-grid">
        <button
          v-for="q in QUANTITY_OPTIONS" :key="q"
          class="qty-btn"
          :class="{ active: quantity === q }"
          @click="quantity = q"
        >{{ q }}</button>
      </div>
    </div>

    <!-- Resolution (conditional) -->
    <div v-if="showResolution" class="section">
      <h3>{{ t('imageGen.resolution') }}</h3>
      <div class="qty-grid">
        <button
          v-for="r in RESOLUTION_OPTIONS" :key="r"
          class="qty-btn"
          :class="{ active: resolution === r }"
          @click="resolution = r"
        >{{ r }}</button>
      </div>
    </div>

    <!-- VidSoda Style (conditional) -->
    <div v-if="showStyle" class="section">
      <h3>{{ t('imageGen.style') }}</h3>
      <div class="style-grid">
        <button
          v-for="s in VIDSODA_STYLES" :key="s.id"
          class="style-card"
          :class="{ active: style === s.id }"
          @click="style = s.id"
        >
          <img :src="s.img" :alt="s.label" class="style-img" loading="lazy" />
          <span class="style-name">{{ s.label }}</span>
        </button>
      </div>
    </div>

    <!-- VidSoda Sharpen (conditional) -->
    <div v-if="showSharpen" class="toggles">
      <label class="toggle">
        <span class="toggle-label">{{ t('imageGen.sharpen') }}</span>
        <input type="checkbox" v-model="sharpen" />
        <span class="switch" />
      </label>
    </div>

    <!-- AiPose HD/FHD (conditional) -->
    <div v-if="showHdFhd" class="section">
      <h3>{{ t('imageGen.resolution') }}</h3>
      <div class="qty-grid">
        <button class="qty-btn" :class="{ active: quality === 'HD' }" @click="quality = 'HD'">HD</button>
        <button class="qty-btn" :class="{ active: quality === 'FHD' }" @click="quality = 'FHD'">FHD</button>
      </div>
    </div>

    <!-- AiPose StyleMode (conditional) -->
    <div v-if="showStyleMode" class="toggles">
      <label class="toggle">
        <span class="toggle-label">Photoreal</span>
        <input type="checkbox" v-model="styleMode" />
        <span class="switch" />
      </label>
    </div>

    <!-- Cost + Generate -->
    <div class="section">
      <button class="cta" :disabled="!canGenerate" @click="generate">
        <span class="cta-text">
          {{ submitting ? t('imageGen.submitting') : t('imageGen.generate') }}
        </span>
        <span class="cost">{{ t('imageGen.creditsCount', { n: totalCost }) }}</span>
      </button>
    </div>

    <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
  </div>
</template>

<style scoped lang="scss">
.panel {
  @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-5 space-y-4 text-white;
}
.head h2 { @apply text-lg font-bold; }
.section { @apply pt-2 space-y-2; }
.section h3 { @apply text-sm font-semibold; }
.prompt-head { @apply flex items-center justify-between; }
.random-btn { @apply text-xs text-amber-400 hover:text-amber-300 transition; }
.section textarea {
  @apply w-full rounded-md border border-neutral-700 bg-neutral-950/60 p-2.5 text-sm text-white placeholder-white/40
    focus:outline-none focus:border-amber-500 resize-y;
}
.model-select {
  @apply w-full rounded-lg bg-neutral-950/60 border border-neutral-700 py-2.5 px-3 text-sm text-white
    focus:outline-none focus:border-amber-500 appearance-none cursor-pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
  option { @apply bg-neutral-900 text-white; }
}
.style-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}
.style-card {
  @apply relative rounded-lg overflow-hidden border-2 border-transparent cursor-pointer transition;
  &.active { @apply border-amber-500; }
  &:hover { @apply border-neutral-500; }
  &.active:hover { @apply border-amber-500; }
}
.style-img {
  @apply w-full aspect-[3/4] object-cover;
}
.style-name {
  @apply absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1 text-[10px] font-medium text-white text-center;
}
.ratio-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
}
.ratio-btn {
  @apply flex flex-row items-center justify-center gap-1.5 rounded-full bg-neutral-950/60 border border-neutral-800 py-2 text-xs transition;
  &.active { @apply bg-white text-neutral-900 border-white; }
}
.ratio-box {
  display: inline-block; flex: none;
  border: 1.5px solid currentColor; border-radius: 2px; opacity: 0.8;
}
.ratio-btn.active .ratio-box { opacity: 1; }
.qty-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
}
.qty-btn {
  @apply rounded-full bg-neutral-950/60 border border-neutral-800 py-2 text-xs font-medium transition text-center;
  &.active { @apply bg-white text-neutral-900 border-white; }
}
.toggles { @apply space-y-2 pt-2; }
.toggle {
  @apply flex items-center justify-between rounded-xl bg-neutral-950/60 border border-neutral-800 px-4 py-3 cursor-pointer;
  input { @apply hidden; }
}
.toggle-label { @apply text-sm font-medium; }
.switch {
  @apply w-10 h-6 rounded-full bg-neutral-700 relative transition;
  &::after {
    content: '';
    @apply absolute size-5 rounded-full bg-white top-0.5 left-0.5 transition-all;
  }
}
.toggle input:checked + .switch { @apply bg-amber-500; }
.toggle input:checked + .switch::after { @apply left-[1.125rem]; }
.cta {
  @apply flex items-center justify-between gap-2 w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-all
    disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
  box-shadow: 0 8px 24px -8px rgba(249,115,22,0.5);
}
.cta-text { @apply flex-1 text-left; }
.cost { @apply rounded-full bg-black/30 px-3 py-0.5 text-xs; }
.err { @apply text-sm text-red-400; }
</style>
